// client/src/App.jsx v4 — Theme-aware shell
import { useState, useEffect, useCallback } from 'react';
import { UserProvider } from './context/UserContext';
import { Archive , FileUp } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  AlertTriangle, TrendingUp, Target, Clock, CheckCircle, FileText,
  Search, Award, DollarSign, Calendar, Users, BarChart2, Activity,
  ArrowUpRight, RefreshCw, Plus, Edit2, Trash2, ChevronUp, ChevronDown,
  Download, LogOut, Settings, Shield, Contact, Eye
} from 'lucide-react';

import { api } from './api';
import BulkClassify from './components/BulkClassify';
import { LOGO_HORIZONTAL_BLACK, LOGO_HORIZONTAL_WHITE, LOGO_STACKED_BLACK, LOGO_STACKED_WHITE } from './logo';
import { fmtCurrency, fmtDate, toInputDate, isOverdue, STATUS_OPTIONS, STATUS_META, ESTIMATOR_COLORS } from './utils';
import { MetricCard, PremiumMetricCard, StatusBadge, ChartTooltip, Card, CardTitle, WinRateDisplay, TimeFilterBar } from './components/UI';
import TenderModal         from './components/TenderModal';
import CommissionDrawer    from './components/CommissionDrawer';
import ActivityLog         from './components/ActivityLog';
import AdminCentre         from './components/AdminCentre';
import CRMPipeline         from './components/CRMPipeline';
import CompanyDirectory    from './components/CRMCompanyDirectory';
import ContactDirectory    from './components/CRMContactDirectory';
import ContractReviewModal from './components/ContractReviewModal';
import ImportPage          from './components/ImportPage';
import TenderInfoModal     from './components/TenderInfoModal';
import ArchivePage         from './components/ArchivePage';
import NotificationsPanel  from './components/NotificationsPanel';
import LoginPage           from './components/LoginPage';
import ForgotPasswordPage  from './components/ForgotPasswordPage';
import ResetPasswordPage   from './components/ResetPasswordPage';
import './styles/wcag-contrast-fix.css';

// Detect reset token in URL on load
const getResetToken = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
};

const ALL_TABS = [
  { id:'summary',  label:'Summary',        icon: BarChart2,   managerOnly: false },
  { id:'workload', label:'Active Workload', icon: Activity,    managerOnly: false },
  { id:'results',  label:'Commercial',      icon: TrendingUp,  managerOnly: false },
  { id:'trends',   label:'Trends',          icon: ArrowUpRight,managerOnly: false },
  { id:'tenders',  label:'All Tenders',     icon: FileText,    managerOnly: false },
  { id:'admin',    label:'Admin',           icon: Shield,      managerOnly: true  },
  { id:'directory',label:'Directory',       icon: Contact,    managerOnly: false },
  { id:'crm',      label:'CRM',             icon: Users,       managerOnly: false },
  { id:'import',   label:'Import',          icon: FileUp,      managerOnly: true  },
];

export default function App() {
  // ── Auth ───────────────────────────────────────────────────────────────────
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ipe_user')); } catch { return null; }
  });

  const handleLogin = (u) => setUser(u);

  // ── Idle timeout (30 minutes) ─────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const IDLE_MS = 30 * 60 * 1000;
    let timer = setTimeout(() => {
      localStorage.removeItem('ipe_token');
      localStorage.removeItem('ipe_user');
      setUser(null);
    }, IDLE_MS);
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => {
      localStorage.removeItem('ipe_token');
      localStorage.removeItem('ipe_user');
      setUser(null);
    }, IDLE_MS); };
    const events = ['mousemove','keydown','mousedown','touchstart','scroll'];
    events.forEach(e => window.addEventListener(e, reset));
    return () => { clearTimeout(timer); events.forEach(e => window.removeEventListener(e, reset)); };
  }, [user]);
  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout', {});
    } catch (_) { /* ignore — token may already be expired */ }
    localStorage.removeItem('ipe_token');
    localStorage.removeItem('ipe_user');
    setUser(null);
  };

  const [authPage, setAuthPage] = useState(() => {
    // If URL contains a reset token, show reset page immediately
    return getResetToken() ? 'reset' : 'login';
  });

  if (!user) {
    if (authPage === 'forgot') {
      return <ForgotPasswordPage onBack={() => setAuthPage('login')} />;
    }
    if (authPage === 'reset') {
      return <ResetPasswordPage
        token={getResetToken()}
        onSuccess={() => {
          // Clear token from URL and go to login
          window.history.replaceState({}, '', '/');
          setAuthPage('login');
        }}
      />;
    }
    return <LoginPage onLogin={handleLogin} onForgotPassword={() => setAuthPage('forgot')} />;
  }

  return <Dashboard user={user} onLogout={handleLogout} />;
}

function Dashboard({ user, onLogout }) {
  const isBD = user?.role === 'bd';
  const isManager = user?.role === 'manager' || user?.role === 'director';
  const isSeniorViewer = user?.role === 'senior_viewer';
  const isViewer = user?.role === 'viewer';
  // JJ tab-matrix refinement: Viewer joins the read-only set.
  const isReadOnly = isBD || isSeniorViewer || isViewer || user?.role === 'admin_clerk';
  // canSeeAV: Viewer never sees AV; BD never sees AV. Everyone else does at
  // the frontend layer (server-side strip still enforces on the wire).
  const canSeeAV = !isBD && !isViewer;
  const [theme, setTheme] = useState(() => localStorage.getItem('ipe_theme') || 'light');
  useEffect(() => { document.documentElement.setAttribute('data-theme', theme); localStorage.setItem('ipe_theme', theme); }, [theme]);
  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');
  const [tab, setTab]               = useState('summary');
  const [lostEscalations, setLostEscalations] = useState([]);
  const [dismissedFeedback, setDismissedFeedback] = useState([]);
  useEffect(() => {
    if (user?.role === 'admin_clerk') setTab('admin');
  }, [user?.role]);

  useEffect(() => {
    if (!['manager','director'].includes(user?.role)) return;
    api.crmLostEscalations().then(data => setLostEscalations(data || [])).catch(() => {});
  }, [user?.role]);
  const [directoryView, setDirectoryView] = useState('companies');
  const [tenders, setTenders]       = useState([]);
  const [estimators, setEstimators] = useState([]);
  const [idnos, setIDNOs]           = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [summary, setSummary]       = useState(null);
  const [commercial, setCommercial] = useState(null);
  const [workload, setWorkload]     = useState(null);
  const [trends, setTrends]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  // Time filters per tab
  const [summaryMonths,    setSummaryMonths]    = useState('fy');
  const [workloadMonths,   setWorkloadMonths]   = useState('fy');
  const [commercialMonths, setCommercialMonths] = useState('fy');
  const [trendsMonths,     setTrendsMonths]     = useState('fy');

  // Modal state
  const [showModal, setShowModal]       = useState(false);
  const [editTender, setEditTender]     = useState(null);
  const [viewActivity, setViewActivity] = useState(null);
  const [exporting, setExporting]       = useState(false);
  const [showTips, setShowTips]         = useState(() => localStorage.getItem('ipe_tips') !== 'off');
  const [showBulkClassify, setShowBulkClassify] = useState(false);
  const [showUserMenu, setShowUserMenu]         = useState(false);
  const [showPwModal, setShowPwModal]           = useState(false);
  const [showCommission, setShowCommission]     = useState(false);
  const [crTender, setCrTender]                 = useState(null);
  const [pwForm, setPwForm]                     = useState({ current:'', next:'', confirm:'' });
  const [pwError, setPwError]                   = useState('');
  const [pwSuccess, setPwSuccess]               = useState(false);
  const [pwSaving, setPwSaving]                 = useState(false);

  // All tenders filters
  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterEst, setFilterEst]       = useState('All');
  const [timelineTab, setTimelineTab]   = useState('resource');
  const [sortField, setSortField]       = useState('deadline');
  const [sortDir, setSortDir]           = useState('asc');
  const [selectedIds, setSelectedIds]   = useState([]);
  const [viewerTender, setViewerTender] = useState(null); // Viewer lightweight info modal

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function toggleSelectAll(ids) {
    setSelectedIds(prev => prev.length === ids.length ? [] : ids);
  }
  async function handleBulkArchive() {
    if (!window.confirm(`Archive ${selectedIds.length} tender(s)?`)) return;
    try {
      await api.post('/api/tenders/bulk-archive', { ids: selectedIds });
      setSelectedIds([]);
      loadAll();
    } catch(e) { alert(e.message || 'Archive failed'); }
  }
  async function handleBulkDelete() {
    if (!window.confirm(`Permanently delete ${selectedIds.length} tender(s)? This cannot be undone.`)) return;
    try {
      await api.post('/api/tenders/bulk-delete', { ids: selectedIds });
      setSelectedIds([]);
      loadAll();
    } catch(e) { alert(e?.response?.data?.error || e.message || 'Delete failed'); }
  }
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, e, i, c] = await Promise.all([
        api.getTenders(), api.getEstimatorUsers(), api.getIDNOs(), api.getClassifications()
      ]);
      setTenders(t); setEstimators(e); setIDNOs(i); setClassifications(c);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const [s, c, w, tr] = await Promise.all([
        api.getSummary(summaryMonths === 'all' ? null : summaryMonths),
        api.getCommercial(commercialMonths === 'all' ? null : commercialMonths),
        api.getWorkload(workloadMonths === 'all' ? null : workloadMonths),
        api.getTrends(trendsMonths === 'all' ? null : trendsMonths),
      ]);
      setSummary(s); setCommercial(c); setWorkload(w); setTrends(tr);
    } catch {}
  }, [summaryMonths, commercialMonths, workloadMonths, trendsMonths]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const handleSave = async (data) => {
    let savedId = editTender?.id;
    if (savedId) {
      await api.updateTender(savedId, data);
    } else {
      const created = await api.createTender(data);
      savedId = created?.id;
      // Update editTender immediately so subsequent saves use PATCH not POST
      if (created) setEditTender(created);
    }
    // Refresh list in background — do NOT touch showModal
    loadAll();
    loadAnalytics();
    const fresh = savedId ? await api.getTender(savedId) : null;
    if (fresh) setEditTender(fresh);
    return fresh;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tender? This cannot be undone.')) return;
    try {
      await api.deleteTender(id);
      setShowModal(false);
      setEditTender(null);
      await loadAll();
      await loadAnalytics();
    } catch (err) {
      alert('Delete failed: ' + (err.message || 'Unknown error'));
    }
  };

  const openEdit = async (tender) => {
    const full = await api.getTender(tender.id);
    // Ensure classifications are loaded before showing modal
    if (!classifications || classifications.length === 0) {
      await loadAll();
    }
    setEditTender(full); setShowModal(true);
  };

  const openActivityLog = async (tender) => {
    const full = await api.getTender(tender.id);
    setViewActivity(full);
  };

  const handleExport = async (format) => {
    const params = {};
    if (filterStatus !== 'All') params.status = filterStatus;
    const estObj = estimators.find(e => e.initials === filterEst);
    if (estObj) params.estimator_user_id = estObj.id;
    try {
      const qs = new URLSearchParams(params).toString();
      const url = `/api/export/${format}${qs ? '?' + qs : ''}`;
      const token = localStorage.getItem('ipe_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `ipe-tenders-${new Date().toISOString().slice(0,10)}.${format}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch(e) { alert('Export failed: ' + e.message); }
    setExporting(false);
  }


  const handleChangePassword = async () => {
    setPwError(''); setPwSuccess(false);
    if (pwForm.next !== pwForm.confirm) { setPwError('Passwords do not match'); return; }
    if (!pwForm.current.trim()) { setPwError('Current password is required'); return; }
    const pwRules = [
      [pwForm.next.length >= 10, 'Password must be at least 10 characters'],
      [/[A-Z]/.test(pwForm.next), 'Password must contain at least one uppercase letter'],
      [/[0-9]/.test(pwForm.next), 'Password must contain at least one number'],
      [/[^A-Za-z0-9]/.test(pwForm.next), 'Password must contain at least one special character'],
    ];
    const fail = pwRules.find(([ok]) => !ok);
    if (fail) { setPwError(fail[1]); return; }
    setPwSaving(true);
    try {
      await api.changePassword(pwForm.current, pwForm.next);
      setPwSuccess(true);
      setPwForm({ current:'', next:'', confirm:'' });
      setTimeout(() => setShowPwModal(false), 1500);
    } catch(e) { setPwError(e.message || 'Password change failed'); }
    finally { setPwSaving(false); }
  };

  const sortedFiltered = tenders.filter(t => {
    if (filterStatus !== 'All' && t.status !== filterStatus) return false;
    if (filterEst !== 'All' && t.estimator_initials !== filterEst) return false;
    if (search) {
      const q = search.toLowerCase();
      return [t.project_name, t.client, t.project_ref, t.estimator_initials].some(v => v?.toLowerCase().includes(q));
    }
    return true;
  }).sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (!av && !bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const SortBtn = ({ field, label }) => (
    <button onClick={() => { setSortField(field); setSortDir(d => field===sortField ? (d==='asc'?'desc':'asc') : 'asc'); }}
      style={{ background:'none', border:'none', color: sortField===field ? '#60a5fa' : '#94a3b8', cursor:'pointer', display:'flex', alignItems:'center', gap:2, fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>
      {label}{sortField===field ? (sortDir==='asc' ? <ChevronUp size={13}/> : <ChevronDown size={13}/>) : null}
    </button>
  );

  const fmtGBP = (v) => v > 0 ? fmtCurrency(v) : '—';

  if (loading && !showModal) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', color:'var(--text-muted)', fontSize:14 }}>
      Loading dashboard…
    </div>
  );

  return (
    <UserProvider user={user} onLogout={onLogout}>
    <>
    <div style={{ minHeight:'100vh' }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="topbar">
        <img src={theme === 'dark' ? LOGO_HORIZONTAL_WHITE : LOGO_HORIZONTAL_BLACK} alt="Ingenious" style={{ height:28, flexShrink:0 }} />

        <nav className="primary-nav">
          {ALL_TABS.filter(t => {
              // REQ-02/04: prefer server-provided capabilities when available.
              // Enforcement is server-side (guards + strip); this filter is UX only.
              const caps = user?.capabilities;
              if (Array.isArray(caps)) {
                const capForTab = {
                  summary:   'view_summary',
                  workload:  'view_workload',
                  results:   'view_commercial',
                  trends:    'view_trends',
                  tenders:   'view_all_tenders',
                  admin:     'view_admin',
                  directory: 'view_directory',
                  crm:       'view_crm',
                  import:    'view_import',
                }[t.id];
                return capForTab ? caps.includes(capForTab) : true;
              }
              // Fallback to role-based rules (users on old JWTs / missing caps).
              const role = user?.role;
              if (role === 'viewer') return false;  // Viewer's dashboard is a follow-up tab
              if (role === 'admin_clerk') return ['crm','admin','import','directory'].includes(t.id);
              if (role === 'bd') return !['results','trends','tenders','admin','import'].includes(t.id);
              if (role === 'estimator') return !['results','trends','crm','admin','import'].includes(t.id);
              if (role === 'senior_viewer') return !['admin','import'].includes(t.id);
              return !t.managerOnly || isManager;
            }).map(t => (
            <button key={t.id} className={`nav-tab${tab===t.id ? ' active' : ''}`} onClick={() => setTab(t.id)} title={t.label}>
              <t.icon size={15}/><span className="nav-tab-label">{t.label}</span>
            </button>
          ))}
        </nav>

        <div className="topbar-right">
          {/* Classify button gated on view_admin (Director + Office Admin only
              after JJ's tab-matrix refinement — Manager no longer sees Admin). */}
          {(user?.capabilities?.includes('view_admin')
            || (!user?.capabilities && isManager)) && (
            <button className="btn btn-accent" onClick={() => setShowBulkClassify(true)} title="Assign classifications">⊞ Classify</button>
          )}
          {/* REQ-02/04: Commission button visibility keyed on capability.
              BD dropped from the list per JJ's confirmation — they lack
              view_aggregate_financials so the drawer would render blank. */}
          {(user?.capabilities?.includes('view_commission_drawer') ||
            (!user?.capabilities && ['director','senior_viewer'].includes(user?.role))) && (
            <button className="btn btn-ghost" onClick={() => setShowCommission(true)}>
              £ Commission
            </button>
          )}
          {!isReadOnly && (
            <button className="btn btn-primary" onClick={() => { setEditTender(null); setShowModal(true); }}>
              <Plus size={15}/> New Tender
            </button>
          )}

          {(isManager || user?.role === 'admin_clerk') && <div style={{ position:'relative' }}>
            <button className="btn btn-ghost" onClick={() => setExporting(v => !v)}>
              <Download size={15}/> Export
            </button>
            {exporting && (
              <div className="dropdown">
                {[['csv','CSV — lightweight'],['xlsx','Excel — formatted']].map(([fmt,lbl]) => (
                  <button key={fmt} className="dropdown-item" onClick={() => { handleExport(fmt); setExporting(false); }}>
                    <FileText size={15} style={{ color:'var(--text-muted)' }}/>{lbl}
                  </button>
                ))}
              </div>
            )}
          </div>}

          <NotificationsPanel currentUser={user} />

          <div style={{ position:'relative' }}>
            <button onClick={() => setShowUserMenu(v => !v)}
              style={{ background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:8, padding:4 }}>
              <div className="avatar">{user.initials}</div>
              <span style={{ color:'var(--text-muted)', fontSize:11 }}>{user.role}</span>
            </button>
            {showUserMenu && (
              <div className="dropdown">
                <div className="dropdown-divider">
                  <p style={{ color:'var(--text)', fontSize:12, fontWeight:600, margin:0 }}>{user.full_name}</p>
                  <p style={{ color:'var(--text-muted)', fontSize:10, margin:'2px 0 0' }}>{user.email}</p>
                </div>
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); setShowPwModal(true); setPwForm({current:'',next:'',confirm:''}); setPwError(''); setPwSuccess(false); }}>
                  🔑 Change Password
                </button>
                <button className="dropdown-item danger" onClick={() => { setShowUserMenu(false); onLogout(); }}>
                  <LogOut size={15}/> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Main content ───────────────────────────────────────────────────── */}
      <main style={{ maxWidth:1400, margin:'0 auto', padding:24, paddingBottom:64 }}>
        {error && <div style={{ background:'#7f1d1d', color:'#fca5a5', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 }}>{error}</div>}

        {/* ── SUMMARY TAB ──────────────────────────────────────────────────── */}
        {tab === 'summary' && summary && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h2 style={{ color:'var(--text)', margin:0, fontSize:18 }}>Portfolio Overview</h2>
              <TimeFilterBar value={summaryMonths} onChange={setSummaryMonths} />
            </div>

            {/* KPI row */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
              <MetricCard title="Active Bids" value={summary.active} icon={Activity} color="#3b82f6"
                sub={`${summary.overdue || 0} overdue`} />
              <MetricCard title="Win Rate" value={summary.win_rate != null ? `${summary.win_rate}%` : '—'} icon={Target} color="#10b981"
                sub={`${summary.won}W / ${(summary.won||0)+(summary.lost||0)} decided`} />
              <MetricCard title="Submitted Awaiting" value={summary.submitted} icon={Clock} color="#f59e0b" />
              <MetricCard title="Won This Period" value={summary.won} icon={Award} color="#10b981" />
            </div>

            {/* Premium value row */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:20 }}>
              {!canSeeAV ? (
                <MetricCard title="Value Won" value={fmtGBP(summary.value_won)} icon={Award} color="#10b981" />
              ) : (
                <PremiumMetricCard title="Value Won"
                  estimateValue={summary.value_won} assetValue={summary.av_won}
                  premiumValue={summary.premium_value_won} color="#10b981" />
              )}
              {!canSeeAV ? (
                <MetricCard title="Pipeline" value={fmtGBP(summary.pipeline_value)} icon={Clock} color="#f59e0b" sub="Submitted-Awaiting" />
              ) : (
                <PremiumMetricCard title="Pipeline (Submitted-Awaiting)"
                  estimateValue={summary.pipeline_value} assetValue={null}
                  premiumValue={summary.pipeline_premium_value} color="#f59e0b" />
              )}
              <MetricCard title="Total Tenders" value={summary.total} icon={FileText} color="#8b5cf6"
                sub={`Won ${summary.won} · Lost ${summary.lost}`} />
            </div>

            {/* Charts row */}
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16, marginBottom:20 }}>
              <Card>
                <CardTitle>Monthly Intake (12 months)</CardTitle>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={summary.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="month" tick={{ fill:'var(--text-muted)', fontSize:10 }} />
                    <YAxis tick={{ fill:'var(--text-muted)', fontSize:10 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize:11 }} />
                    <Bar dataKey="received" name="Received" fill="#3b82f6" radius={[3,3,0,0]} />
                    <Bar dataKey="won" name="Won" fill="#10b981" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              {/* Status breakdown — donut */}
              <Card>
                <CardTitle>Status Breakdown</CardTitle>
                {(() => {
                  const STATUS_DONUT_COLORS = {'Bidding':'#3B82F6','Submitted-Awaiting':'#F59E0B','Won':'#10B981','Lost':'#EF4444','Dead':'#6B7280'};
                  const statusData = [['Bidding',summary.active],['Submitted-Awaiting',summary.submitted],['Won',summary.won],['Lost',summary.lost],['Dead',summary.dead]]
                    .filter(([,v]) => v > 0)
                    .map(([name, value]) => ({ name, value }));
                  const total = statusData.reduce((s, d) => s + d.value, 0);
                  return statusData.length > 0 ? (
                    <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                      <ResponsiveContainer width="50%" height={180}>
                        <PieChart>
                          <Pie data={statusData} dataKey="value" nameKey="name"
                            cx="50%" cy="50%" outerRadius={75} innerRadius={38}
                            paddingAngle={statusData.length > 1 ? 2 : 0}
                            startAngle={90} endAngle={-270}>
                            {statusData.map((d, i) => (
                              <Cell key={i} fill={STATUS_DONUT_COLORS[d.name] || '#6B7280'} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v, name) => [`${v} (${total > 0 ? Math.round(v/total*100) : 0}%)`, name]}
                            contentStyle={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, fontSize:11, boxShadow:'var(--dropdown-shadow)' }}
                            itemStyle={{ color:'var(--text)' }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        {statusData.map(d => (
                          <div key={d.name} style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{ width:10, height:10, borderRadius:'50%', background: STATUS_DONUT_COLORS[d.name] || '#6B7280', flexShrink:0 }} />
                            <span style={{ color:'var(--text-muted)', fontSize:11, minWidth:110 }}>{d.name}</span>
                            <span style={{ color:'var(--text)', fontSize:13, fontWeight:600 }}>{d.value}</span>
                            <span style={{ color:'var(--text-muted)', fontSize:10 }}>({total > 0 ? Math.round(d.value/total*100) : 0}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : <p style={{ color:'var(--text-muted)', fontSize:12 }}>No data</p>;
                })()}
              </Card>
            </div>

            {/* Classification breakdown — two donuts */}
            {summary.by_classification?.length > 0 && (
              <Card style={{ marginBottom:16 }}>
                <CardTitle>Work Classification Breakdown</CardTitle>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:24 }}>

                  {/* Donut 1: All tenders */}
                  <div>
                    <p style={{ color:'var(--text-muted)', fontSize:11, textAlign:'center', margin:'0 0 8px', textTransform:'uppercase', letterSpacing:0.6 }}>All Tenders</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={summary.by_classification} dataKey="total" nameKey="classification"
                          cx="50%" cy="50%" outerRadius={85} innerRadius={40} paddingAngle={2}>
                          {summary.by_classification.map((_, i) => (
                            <Cell key={i} fill={ESTIMATOR_COLORS[i % ESTIMATOR_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, _, props) => [`${v} tenders`, props.payload.classification]}
                          contentStyle={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, fontSize:11, boxShadow:'var(--dropdown-shadow)' }}
                          labelStyle={{ color:'var(--text-secondary)', fontWeight:600 }}
                          itemStyle={{ color:'var(--text)' }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display:'flex', flexDirection:'column', gap:5, marginTop:8 }}>
                      {summary.by_classification.map((c, i) => {
                        const total = summary.by_classification.reduce((s,x) => s+x.total, 0);
                        const pct = total > 0 ? Math.round(c.total/total*100) : 0;
                        return (
                          <div key={c.classification} style={{ display:'flex', alignItems:'center', gap:7 }}>
                            <div style={{ width:8, height:8, borderRadius:'50%', background:ESTIMATOR_COLORS[i%ESTIMATOR_COLORS.length], flexShrink:0 }} />
                            <span style={{ color:'var(--text-dim)', fontSize:10, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={c.classification}>{c.classification}</span>
                            <span style={{ color:'var(--text)', fontSize:10, fontWeight:600 }}>{c.total}</span>
                            <span style={{ color:'var(--text-muted)', fontSize:10, width:26, textAlign:'right' }}>{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Donut 2: Won tenders */}
                  <div>
                    <p style={{ color:'var(--text-muted)', fontSize:11, textAlign:'center', margin:'0 0 8px', textTransform:'uppercase', letterSpacing:0.6 }}>Won Tenders</p>
                    {summary.won_by_classification?.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie data={summary.won_by_classification} dataKey="won" nameKey="classification"
                              cx="50%" cy="50%" outerRadius={85} innerRadius={40} paddingAngle={2}>
                              {summary.won_by_classification.map((_, i) => (
                                <Cell key={i} fill={ESTIMATOR_COLORS[i % ESTIMATOR_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v, _, props) => [`${v} won · ${fmtGBP(props.payload.value_won)}`, props.payload.classification]}
                              contentStyle={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, fontSize:11, boxShadow:'var(--dropdown-shadow)' }}
                              labelStyle={{ color:'var(--text-secondary)', fontWeight:600 }}
                              itemStyle={{ color:'var(--text)' }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ display:'flex', flexDirection:'column', gap:5, marginTop:8 }}>
                          {summary.won_by_classification.map((c, i) => {
                            const total = summary.won_by_classification.reduce((s,x) => s+x.won, 0);
                            const pct = total > 0 ? Math.round(c.won/total*100) : 0;
                            return (
                              <div key={c.classification} style={{ display:'flex', alignItems:'center', gap:7 }}>
                                <div style={{ width:8, height:8, borderRadius:'50%', background:ESTIMATOR_COLORS[i%ESTIMATOR_COLORS.length], flexShrink:0 }} />
                                <span style={{ color:'var(--text-dim)', fontSize:10, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={c.classification}>{c.classification}</span>
                                <span style={{ color:'#10b981', fontSize:10, fontWeight:600 }}>{c.won}</span>
                                <span style={{ color:'var(--text-muted)', fontSize:10, width:26, textAlign:'right' }}>{pct}%</span>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:220 }}>
                        <p style={{ color:'var(--text-dim)', fontSize:12 }}>No won tenders yet</p>
                      </div>
                    )}
                  </div>

                  {/* Donut 3: Submitted Win Rate by Classification */}
                  <div style={{ borderLeft:'2px solid rgba(6,182,212,0.25)', paddingLeft:18 }}>
                    <p style={{ color:'#06b6d4', fontSize:11, fontWeight:600, textAlign:'center', margin:'0 0 8px', textTransform:'uppercase', letterSpacing:0.6 }}>Submitted Win Rate</p>
                    {(() => {
                      const submittedWR = (summary.by_classification || []).map(c => ({
                        classification: c.classification,
                        submitted: (c.won || 0) + (c.lost || 0),
                        won: c.won || 0,
                        wr: ((c.won || 0) + (c.lost || 0)) > 0 ? Math.round((c.won / ((c.won || 0) + (c.lost || 0))) * 100) : 0,
                      })).filter(c => c.submitted > 0);
                      const CYAN_COLORS = ['#06b6d4','#0891b2','#22d3ee','#0e7490','#67e8f9','#155e75','#0284c7','#38bdf8'];
                      const totalDecided = submittedWR.reduce((s,c) => s + c.submitted, 0);
                      const totalWon = submittedWR.reduce((s,c) => s + c.won, 0);
                      const overallWR = totalDecided > 0 ? Math.round(totalWon / totalDecided * 100) : null;
                      if (!submittedWR.length) return (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:220 }}>
                          <p style={{ color:'var(--text-dim)', fontSize:12 }}>No decided tenders yet</p>
                        </div>
                      );
                      return (
                        <>
                          <div style={{ position:'relative' }}>
                            <ResponsiveContainer width="100%" height={220}>
                              <PieChart>
                                <Pie data={submittedWR} dataKey="wr" nameKey="classification"
                                  cx="50%" cy="50%" outerRadius={85} innerRadius={40} paddingAngle={2}>
                                  {submittedWR.map((_, i) => (
                                    <Cell key={i} fill={CYAN_COLORS[i % CYAN_COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(v, _, props) => [`${v}% win rate (${props.payload.won}W / ${props.payload.submitted} decided)`, props.payload.classification]}
                                  contentStyle={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, fontSize:11, boxShadow:'var(--dropdown-shadow)' }}
                                  labelStyle={{ color:'var(--text-secondary)', fontWeight:600 }}
                                  itemStyle={{ color:'var(--text)' }} />
                              </PieChart>
                            </ResponsiveContainer>
                            {overallWR !== null && (
                              <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', textAlign:'center', pointerEvents:'none' }}>
                                <div style={{ color:'#ffffff', fontSize:20, fontWeight:700 }}>{overallWR}%</div>
                                <div style={{ color:'var(--text-muted)', fontSize:10, marginTop:2 }}>overall</div>
                              </div>
                            )}
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap:5, marginTop:8 }}>
                            {submittedWR.map((c, i) => (
                              <div key={c.classification} style={{ display:'flex', alignItems:'center', gap:7 }}>
                                <div style={{ width:8, height:8, borderRadius:'50%', background:CYAN_COLORS[i%CYAN_COLORS.length], flexShrink:0 }} />
                                <span style={{ color:'var(--text-dim)', fontSize:10, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={c.classification}>{c.classification}</span>
                                <span style={{ color:'#06b6d4', fontSize:10, fontWeight:600 }}>{c.wr}%</span>
                                <span style={{ color:'var(--text-muted)', fontSize:10, width:36, textAlign:'right' }}>{c.won}W/{c.submitted}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                </div>
              </Card>
            )}
            {/* Estimator performance */}
            <Card style={{ marginBottom:16 }}>
              <CardTitle>Estimator Performance</CardTitle>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12 }}>
                {summary.estimators?.map((e, i) => {
                  const decided = e.won + e.lost;
                  const wr = decided > 0 ? Math.round(e.won/decided*100) : null;
                  return (
                    <div key={e.id} style={{ background:'var(--surface2)', borderRadius:10, padding:'14px 16px', borderLeft:`3px solid ${ESTIMATOR_COLORS[i%ESTIMATOR_COLORS.length]}` }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                        <span style={{ color:'var(--text)', fontWeight:700 }}>{e.initials}</span>
                        <span style={{ color:'var(--text-muted)', fontSize:11 }}>{e.total} tenders</span>
                      </div>
                      <div style={{ display:'flex', gap:12, fontSize:11 }}>
                        <span style={{ color:'#10b981' }}>{e.won}W</span>
                        <span style={{ color:'#ef4444' }}>{e.lost}L</span>
                        <span style={{ color:'var(--text-muted)' }}>{wr != null ? `${wr}% WR` : '—'}</span>
                      </div>
                      <p style={{ color:'var(--text-muted)', fontSize:11, margin:'4px 0 0' }}>{fmtGBP(e.value_won)}</p>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Project Start Timeline */}
            {(() => {
              // Build 18-month rolling window from today
              const now = new Date();
              const months = [];
              for (let i = 0; i < 18; i++) {
                const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                months.push(`${d.getFullYear()}-${mm}`); // YYYY-MM local
              }
              const cutoff = months[0];
              const ec = new Date(now.getFullYear(), now.getMonth() + 18, 1);
              const endCutoff = `${ec.getFullYear()}-${String(ec.getMonth()+1).padStart(2,'0')}`;

              // Filter tenders with planned_start_date in window
              const relevant = (tenders || []).filter(t =>
                t.planned_start_date &&
                t.planned_start_date >= cutoff &&
                t.planned_start_date < endCutoff &&
                !['Dead','Paused'].includes(t.status)
              );

              if (!relevant.length) return null;

              const data = months.map(month => {
                const inMonth = relevant.filter(t => t.planned_start_date?.slice(0,7) === month);
                const won = inMonth.filter(t => t.status === 'Won');
                const submitted = inMonth.filter(t => t.status === 'Submitted-Awaiting');
                const bidding = inMonth.filter(t => t.status === 'Bidding' || t.status === 'Revision Required');
                const label = new Date(month + '-01').toLocaleDateString('en-GB', { month:'short', year:'2-digit' });
                return {
                  month: label,
                  won_count: won.length,
                  submitted_count: submitted.length,
                  bidding_count: bidding.length,
                  won_value: won.reduce((s,t) => s + (t.price || 0), 0),
                  submitted_value: submitted.reduce((s,t) => s + (t.price || 0), 0),
                  bidding_value: bidding.reduce((s,t) => s + (t.price || 0), 0),
                };
              });

              const isResource = timelineTab === 'resource';
              const wonKey = isResource ? 'won_count' : 'won_value';
              const submittedKey = isResource ? 'submitted_count' : 'submitted_value';
              const biddingKey = isResource ? 'bidding_count' : 'bidding_value';
              const fmtY = v => isResource ? v : `£${(v/1000).toFixed(0)}k`;

              return (
                <Card style={{ marginBottom:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                    <CardTitle style={{ margin:0 }}>Project Start Timeline <span style={{ color:'var(--text-muted)', fontWeight:400, fontSize:11 }}>— next 18 months</span></CardTitle>
                    <div style={{ display:'flex', gap:6 }}>
                      {['resource','cashflow'].map(t => (
                        <button key={t} onClick={() => setTimelineTab(t)}
                          style={{ padding:'5px 12px', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer',
                            background: timelineTab === t ? 'rgba(59,130,246,0.15)' : 'transparent',
                            border: `1px solid ${timelineTab === t ? 'rgba(59,130,246,0.4)' : 'rgba(255,255,255,0.1)'}`,
                            color: timelineTab === t ? '#60a5fa' : '#64748b' }}>
                          {t === 'resource' ? 'Resource (Count)' : 'Cash Flow (£)'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data} margin={{ top:4, right:8, left:8, bottom:4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="month" tick={{ fill:'var(--text-muted)', fontSize:10 }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtY} tick={{ fill:'var(--text-muted)', fontSize:10 }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, fontSize:11 }}
                        labelStyle={{ color:'var(--text-secondary)', fontWeight:600 }}
                        itemStyle={{ color:'var(--text)' }}
                        formatter={(v, name) => isResource ? [`${v} project${v!==1?'s':''}`, name] : [fmtCurrency(v), name]}
                      />
                      <Bar dataKey={wonKey} name="Won — Committed" stackId="a" fill="rgba(16,185,129,0.75)" stroke="#10b981" strokeWidth={1} radius={[0,0,0,0]} />
                      <Bar dataKey={submittedKey} name="Submitted-Awaiting" stackId="a" fill="rgba(148,163,184,0.55)" stroke="#94a3b8" strokeWidth={1} />
                      <Bar dataKey={biddingKey} name="Bidding — Projected" stackId="a" fill="rgba(51,65,85,0.85)" stroke="#475569" strokeWidth={1} radius={[3,3,0,0]} />
                      <Line type="monotone"
                        dataKey={d => (d[wonKey]||0) + (d[submittedKey]||0) + (d[biddingKey]||0)}
                        stroke="#f59e0b" strokeWidth={2} dot={{ fill:'#f59e0b', r:3 }} activeDot={{ r:5 }}
                        name="Total" legendType="none" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ display:'flex', gap:16, marginTop:10, flexWrap:'wrap' }}>
                    {[
                      { color:'rgba(16,185,129,0.75)', border:'#10b981', label:'Won — Committed' },
                      { color:'rgba(148,163,184,0.55)', border:'#94a3b8', label:'Submitted-Awaiting' },
                      { color:'rgba(51,65,85,0.85)', border:'#475569', label:'Bidding — Projected' },
                      { color:'#f59e0b', border:'#f59e0b', label:'Peak load', circle:true },
                    ].map(({ color, label, circle }) => (
                      <div key={label} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'var(--text-dim)' }}>
                        <div style={{ width:10, height:10, borderRadius: circle ? '50%' : 2, background:color, flexShrink:0 }} />
                        {label}
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })()}

            {/* Missing lost feedback flag */}
            {(() => {
              const visibleFeedback = (summary.missing_feedback || []).filter(t => !dismissedFeedback.includes(t.id));
              return visibleFeedback.length > 0 && (
                <Card style={{ marginBottom:16, border:'1px solid rgba(234,179,8,0.2)' }}>
                  <CardTitle><span style={{ color:'#eab308' }}>⚠ Lost Feedback Outstanding ({visibleFeedback.length})</span></CardTitle>
                  <p style={{ color:'var(--text-muted)', fontSize:12, margin:'0 0 10px' }}>These lost tenders are missing client/prospect feedback. Click to open and add feedback.</p>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <tbody>
                      {visibleFeedback.map(t => (
                        <tr key={t.id} style={{ borderBottom:'1px solid var(--border)' }}>
                          <td style={{ padding:'6px 0', color:'var(--text)', cursor:'pointer' }} onClick={() => setTab('tenders')}>{t.project_ref} {t.project_name}</td>
                          <td style={{ padding:'6px 8px', color:'var(--text-dim)', cursor:'pointer' }} onClick={() => setTab('tenders')}>{t.client}</td>
                          <td style={{ padding:'6px 0', color:'var(--text-muted)', textAlign:'right', cursor:'pointer' }} onClick={() => setTab('tenders')}>{t.estimator || '—'}</td>
                          {user?.role === 'director' && (
                            <td style={{ padding:'6px 0 6px 12px', textAlign:'right' }}>
                              <button onClick={() => setDismissedFeedback(p => [...p, t.id])}
                                style={{ padding:'2px 8px', borderRadius:4, border:'1px solid rgba(234,179,8,0.3)', background:'transparent', color:'#eab308', fontSize:10, cursor:'pointer', fontFamily:'inherit' }}>
                                Dismiss
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              );
            })()}

            {/* Overdue */}
            {(summary.overdue_list?.length || 0) > 0 && (
              <Card>
                <CardTitle><span style={{ color:'#ef4444' }}>⚠ Overdue Bids ({summary.overdue_list?.length || 0})</span></CardTitle>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <tbody>
                    {summary.overdue_list.map(t => (
                      <tr key={t.id}>
                        <td style={{ padding:'6px 0', color:'var(--text)' }}>{t.project_ref} {t.project_name}</td>
                        <td style={{ padding:'6px 8px', color:'var(--text-dim)' }}>{t.client}</td>
                        <td style={{ padding:'6px 0', color:'#ef4444', textAlign:'right' }}>{fmtDate(t.deadline)}</td>
                        <td style={{ padding:'6px 0 6px 12px', color:'var(--text-muted)', textAlign:'right' }}>{t.estimator || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>
        )}

        {/* ── WORKLOAD TAB ─────────────────────────────────────────────────── */}
        {tab === 'workload' && workload && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h2 style={{ color:'var(--text)', margin:0, fontSize:18 }}>Active Workload</h2>
              <TimeFilterBar value={workloadMonths} onChange={setWorkloadMonths} />
            </div>

            {/* Cycle time cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
              {[
                { label:'Receipt → Manager', value: workload.cycle_times?.avg_receipt_to_manager, target:3 },
                { label:'Manager → Approval', value: workload.cycle_times?.avg_manager_to_approval, target:2 },
                { label:'Approval → Submit', value: workload.cycle_times?.avg_approval_to_submit, target:5 },
                { label:'Total Cycle', value: workload.cycle_times?.avg_total_cycle, target:14 },
              ].map(({ label, value, target }) => {
                const v = value ? Math.round(value) : null;
                const over = v != null && v > target;
                return (
                  <div key={label} style={{ background:'var(--card-bg)', borderRadius:12, padding:'16px 18px', border:`1px solid ${over ? 'rgba(239,68,68,0.2)' : 'var(--border)'}` }}>
                    <p style={{ color:'var(--text-muted)', fontSize:11, margin:'0 0 6px', textTransform:'uppercase', letterSpacing:1 }}>{label}</p>
                    <p style={{ color: over ? '#ef4444' : 'var(--text)', fontSize:24, fontWeight:700, margin:0 }}>{v != null ? `${v}d` : '—'}</p>
                    <p style={{ color:'var(--text-muted)', fontSize:11, margin:'4px 0 0' }}>Target: {target}d</p>
                  </div>
                );
              })}
            </div>

            {/* Active bids table */}
            <Card>
              <CardTitle>Active Bids ({workload.active?.length || 0})</CardTitle>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid var(--border)' }}>
                      {['Ref','Project','Client','Classification','Estimator','Deadline','Estimator Progress','POC'].map(h => (
                        <th key={h} style={{ padding:'8px 10px', color:'var(--text-dim)', fontWeight:600, textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {workload.active?.map(t => {
                      const daysLeft = t.days_until_deadline;
                      const overdue = daysLeft !== null && daysLeft < 0;
                      const pct = t.pct_used ?? null;
                      const daysUsed = t.days_used ?? null;
                      const target = t.target_working_days ?? t.target_days ?? 10;
                      const daysRemaining = t.days_remaining_working ?? null;
                      const pocOutstanding = t.poc_outstanding;

                      // Progress colour
                      const progressColor = pct === null ? '#475569'
                        : pct >= 100 ? '#ef4444'
                        : pct >= 80  ? '#f59e0b'
                        : '#10b981';

                      // Progress label
                      const progressLabel = t.clock_start === null
                        ? 'Awaiting POC'
                        : pct === null ? '—'
                        : pct >= 100
                          ? `${Math.abs(daysRemaining)}d over target`
                          : `${daysRemaining}d left / ${target}d target`;

                      return (
                        <tr key={t.id} style={{ borderBottom:'1px solid var(--border)' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--hover-bg)'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          <td style={{ padding:'8px 10px', color:'var(--text-secondary)' }}>{t.project_ref}</td>
                          <td style={{ padding:'8px 10px', color:'var(--text)', maxWidth:200 }}>{t.project_name}</td>
                          <td style={{ padding:'8px 10px', color:'var(--text-secondary)', maxWidth:160 }}>{t.client}</td>
                          <td style={{ padding:'8px 10px', color:'var(--text-dim)', fontSize:11 }}>{t.classification_name || '—'}</td>
                          <td style={{ padding:'8px 10px', color:'var(--text-secondary)' }}>{t.estimator_initials || '—'}</td>
                          <td style={{ padding:'8px 10px', color: overdue ? '#ef4444' : '#94a3b8' }}>
                            {fmtDate(t.deadline)}
                            {overdue && <span style={{ color:'#ef4444', fontSize:10, marginLeft:6, fontWeight:600 }}>{Math.abs(Math.round(daysLeft))}d overdue</span>}
                          </td>
                          <td style={{ padding:'8px 10px', minWidth:180 }}>
                            {t.clock_start === null ? (
                              <span style={{ color:'var(--text-muted)', fontSize:11, fontStyle:'italic' }}>Clock paused — POC outstanding</span>
                            ) : (
                              <div>
                                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                                  <span style={{ color: progressColor, fontSize:11, fontWeight:600 }}>{progressLabel}</span>
                                  {pct !== null && <span style={{ color:'var(--text-muted)', fontSize:10 }}>{Math.min(pct,100)}%</span>}
                                </div>
                                <div style={{ height:5, background:'var(--surface2)', borderRadius:3, overflow:'hidden' }}>
                                  <div style={{ height:'100%', width:`${Math.min(pct||0, 100)}%`, background:progressColor, borderRadius:3, transition:'width 0.4s' }} />
                                </div>
                              </div>
                            )}
                          </td>
                          <td style={{ padding:'8px 10px', textAlign:'center' }}>
                            {pocOutstanding ? (
                              <span title={`POC Application Required — ${t.poc_type || 'type not set'}${t.poc_days_elapsed != null ? ` · ${t.poc_days_elapsed}d elapsed` : ''}`}
                                style={{ background:'rgba(234,179,8,0.15)', color:'#fbbf24', fontSize:10, fontWeight:700,
                                  padding:'2px 7px', borderRadius:10, border:'1px solid rgba(234,179,8,0.3)', whiteSpace:'nowrap', cursor:'default' }}>
                                ⏳ POC
                              </span>
                            ) : t.poc_status === 'Received' ? (
                              <span style={{ color:'#10b981', fontSize:10, fontWeight:600 }}>✓ Received</span>
                            ) : (
                              <span style={{ color:'var(--text-muted)', fontSize:10 }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ── COMMERCIAL TAB ───────────────────────────────────────────────── */}
        {tab === 'results' && commercial && (
          <div>
            {['manager','director'].includes(user?.role) && lostEscalations.length > 0 && (
              <div style={{ marginBottom: 20, padding: '10px 16px', background: 'rgba(239,68,68,0.06)',
                border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#dc2626' }}>
                    {lostEscalations.length} lost lead{lostEscalations.length > 1 ? 's' : ''} missing feedback
                  </span>
                  <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>
                    No lost reason captured after 10+ days:
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>
                    {lostEscalations.slice(0,3).map(l => l.enq_ref || l.title).join(', ')}
                    {lostEscalations.length > 3 ? ` +${lostEscalations.length - 3} more` : ''}
                  </span>
                </div>
                <button onClick={() => setTab('crm')}
                  style={{ padding: '5px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#dc2626', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                  Go to CRM →
                </button>
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h2 style={{ color:'var(--text)', margin:0, fontSize:18 }}>Commercial Performance</h2>
              <TimeFilterBar value={commercialMonths} onChange={setCommercialMonths} />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
              <MetricCard title="Win Rate" value={commercial.win_rate != null ? `${commercial.win_rate}%` : '—'} icon={Target} color="#10b981"
                sub={`${commercial.won}W / ${commercial.decided} decided`} />
              <MetricCard title="Won" value={commercial.won} icon={Award} color="#10b981" sub={fmtGBP(commercial.value_won)} />
              <MetricCard title="Lost" value={commercial.lost} icon={AlertTriangle} color="#ef4444" sub={fmtGBP(commercial.value_lost)} />
              {!canSeeAV ? (
                <MetricCard title="Value Won" value={fmtGBP(commercial.value_won)} icon={Award} color="#10b981" sub="Estimate value" />
              ) : (
                <PremiumMetricCard title="Won Value"
                  estimateValue={commercial.value_won} assetValue={commercial.av_won}
                  premiumValue={commercial.premium_won} color="#10b981" />
              )}
            </div>

            {/* IDNO AV infographic — hidden from BD */}
            {canSeeAV && commercial.idno_av?.length > 0 && (
              <Card style={{ marginBottom:16 }}>
                <CardTitle>IDNO Asset Value Distribution</CardTitle>
                <p style={{ color:'var(--text-muted)', fontSize:12, margin:'0 0 16px' }}>AV exposure by IDNO — shows how pipeline risk is spread</p>
                {(() => {
                  const chartData = commercial.idno_av.map((row, i) => ({
                    idno: row.idno,
                    av_won: parseFloat(row.av_won) || 0,
                    av_pipeline: parseFloat(row.av_pipeline) || 0,
                    total_av: parseFloat(row.total_av) || 0,
                    won: row.won,
                    total: row.total,
                    color: ESTIMATOR_COLORS[i % ESTIMATOR_COLORS.length],
                  }));
                  return (
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
                      <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 52)}>
                        <BarChart data={chartData} layout="vertical" margin={{ left:8, right:40, top:4, bottom:4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                          <XAxis type="number" tickFormatter={v => `£${(v/1000).toFixed(0)}k`}
                            tick={{ fill:'var(--text-muted)', fontSize:10 }} axisLine={false} tickLine={false} />
                          <YAxis type="category" dataKey="idno" tick={{ fill:'var(--text)', fontSize:12, fontWeight:500 }}
                            axisLine={false} tickLine={false} width={90} />
                          <Tooltip formatter={(v, name) => [fmtCurrency(v), name]}
                            contentStyle={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, fontSize:11 }}
                            itemStyle={{ color:'var(--text)' }} />
                          <Bar dataKey="av_won" name="Won AV" stackId="a" fill="#10b981" radius={[0,0,0,0]} />
                          <Bar dataKey="av_pipeline" name="Pipeline AV" stackId="a" fill="#f59e0b" radius={[0,3,3,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                      <div style={{ display:'flex', flexDirection:'column', gap:10, justifyContent:'center' }}>
                        {chartData.map(row => (
                          <div key={row.idno} style={{ background:'var(--surface2)', borderRadius:8, padding:'10px 14px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                              <span style={{ color:'var(--text)', fontSize:12, fontWeight:600 }}>{row.idno}</span>
                              <span style={{ color:'var(--text-muted)', fontSize:11 }}>{row.won}/{row.total} tenders</span>
                            </div>
                            <div style={{ display:'flex', gap:14, fontSize:11 }}>
                              <span style={{ color:'#10b981' }}>Won: {fmtGBP(row.av_won)}</span>
                              <span style={{ color:'#f59e0b' }}>Pipeline: {fmtGBP(row.av_pipeline)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </Card>
            )}

            {/* Value bands */}
            <Card style={{ marginBottom:16 }}>
              <CardTitle>Value Band Analysis</CardTitle>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
                {commercial.bands?.map(b => {
                  const decided = b.won + b.lost;
                  const wr = decided > 0 ? Math.round(b.won/decided*100) : null;
                  return (
                    <div key={b.band} style={{ background:'var(--surface2)', borderRadius:10, padding:'14px 16px' }}>
                      <p style={{ color:'var(--text-dim)', fontSize:12, margin:'0 0 8px', fontWeight:600 }}>{b.band}</p>
                      <p style={{ color:'var(--text)', fontSize:20, fontWeight:700, margin:0 }}>{b.total} tenders</p>
                      <div style={{ display:'flex', gap:10, fontSize:11, margin:'4px 0' }}>
                        <span style={{ color:'#10b981' }}>{b.won}W</span>
                        <span style={{ color:'#ef4444' }}>{b.lost}L</span>
                        {wr != null && <span style={{ color:'var(--text-muted)' }}>{wr}% WR</span>}
                      </div>
                      <p style={{ color:'var(--text-muted)', fontSize:11, margin:0 }}>{fmtGBP(b.value_won)} won</p>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Won tenders */}
            <Card>
              <CardTitle>Won Tenders</CardTitle>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid var(--border)' }}>
                      {(!canSeeAV ? ['Project','Client','Classification','Estimator','Estimate','Decision'] : ['Project','Client','Classification','Estimator','IDNO','Estimate','AV','Premium','Decision']).map(h => (
                        <th key={h} style={{ padding:'7px 10px', color:'var(--text-dim)', fontWeight:600, textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {commercial.won_tenders?.map(t => (
                      <tr key={t.id} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'7px 10px', color:'var(--text)' }}>{t.project_name}</td>
                        <td style={{ padding:'7px 10px', color:'var(--text-dim)' }}>{t.client}</td>
                        <td style={{ padding:'7px 10px', color:'var(--text-muted)', fontSize:11 }}>{t.classification || '—'}</td>
                        <td style={{ padding:'7px 10px', color:'var(--text-dim)' }}>{t.estimator || '—'}</td>
                        {canSeeAV && <td style={{ padding:'7px 10px', color:'#60a5fa', fontSize:11 }}>{t.idno || '—'}</td>}
                        <td style={{ padding:'7px 10px', color:'var(--text)' }}>{fmtGBP(t.price)}</td>
                        {canSeeAV && <td style={{ padding:'7px 10px', color:'#60a5fa' }}>{fmtGBP(t.asset_value)}</td>}
                        {canSeeAV && <td style={{ padding:'7px 10px', color:'#10b981', fontWeight:600 }}>{fmtGBP(t.premium_value)}</td>}
                        <td style={{ padding:'7px 10px', color:'var(--text-muted)' }}>{fmtDate(t.decision_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ── TRENDS TAB ───────────────────────────────────────────────────── */}
        {tab === 'trends' && trends && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h2 style={{ color:'var(--text)', margin:0, fontSize:18 }}>Submission Trends</h2>
              <TimeFilterBar value={trendsMonths} onChange={setTrendsMonths} />
            </div>

            <Card style={{ marginBottom:16 }}>
              <CardTitle>Monthly Submissions vs Won</CardTitle>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={trends.monthly_submissions}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fill:'var(--text-muted)', fontSize:10 }} />
                  <YAxis tick={{ fill:'var(--text-muted)', fontSize:10 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize:11 }} />
                  <Bar dataKey="submitted" name="Submitted" fill="#3b82f6" radius={[3,3,0,0]} />
                  <Bar dataKey="won" name="Won" fill="#10b981" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card style={{ marginBottom:16 }}>
              <CardTitle>Submitted Value vs Won Value (£)</CardTitle>
              {(() => {
                const maxVal = Math.max(...(trends.monthly_submissions||[]).map(r => parseFloat(r.value_submitted||0)));
                const yMax = Math.ceil(maxVal * 1.1 / 100000) * 100000;
                const fmtY = v => v >= 1000000 ? `£${(v/1000000).toFixed(1)}M` : `£${(v/1000).toFixed(0)}k`;
                return (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trends.monthly_submissions}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fill:'var(--text-muted)', fontSize:10 }} />
                  <YAxis tickFormatter={fmtY} tick={{ fill:'var(--text-muted)', fontSize:10 }} width={80} domain={[0, yMax]}/>
                  <Tooltip formatter={(v, name) => [`£${Math.round(v||0).toLocaleString('en-GB')}`, name]} contentStyle={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, fontSize:11 }} />
                  <Legend wrapperStyle={{ fontSize:11 }} />
                  <Line type="linear" dataKey="value_submitted" name="Submitted Value" stroke="#3b82f6" dot={{ r:3, fill:'#3b82f6' }} strokeWidth={2} />
                  <Line type="linear" dataKey="value_won" name="Won Value" stroke="#10b981" dot={{ r:3, fill:'#10b981' }} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
                );
              })()}
            </Card>

            <Card>
              <CardTitle>Estimator Win Rates</CardTitle>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12 }}>
                {trends.estimator_rates?.map((e, i) => {
                  const decided = e.won + e.lost;
                  const wr = decided > 0 ? Math.round(e.won/decided*100) : null;
                  return (
                    <div key={e.initials} style={{ background:'var(--surface2)', borderRadius:10, padding:'14px 16px', borderLeft:`3px solid ${ESTIMATOR_COLORS[i%ESTIMATOR_COLORS.length]}` }}>
                      <p style={{ color:'var(--text)', fontWeight:700, margin:'0 0 6px' }}>{e.initials}</p>
                      <p style={{ color:'var(--text)', fontSize:22, fontWeight:700, margin:0 }}>{wr != null ? `${wr}%` : '—'}</p>
                      <p style={{ color:'var(--text-muted)', fontSize:11, margin:'4px 0 0' }}>{e.won}W / {decided} decided</p>
                      <p style={{ color:'var(--text-muted)', fontSize:11, margin:'2px 0 0' }}>{fmtGBP(e.value_won)} won</p>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* ── ALL TENDERS TAB ──────────────────────────────────────────────── */}
        {tab === 'tenders' && (
          <div>
            {/* Viewer-only helper banner. The row itself is the primary
                affordance now — cursor+hover on the tr, plus a sticky-right
                View pill so the button doesn't scroll out of viewport. */}
            {isViewer && (
              <div style={{ marginBottom:12, padding:'8px 12px', background:'rgba(14,154,170,0.08)', border:'1px solid rgba(14,154,170,0.28)', borderRadius:6, color:'var(--text-dim)', fontSize:12, display:'flex', alignItems:'center', gap:8 }}>
                <Eye size={14} style={{ color:'#0e9aaa', flexShrink:0 }}/>
                <span>Click any row to see full detail — site address, What3Words, and scope.</span>
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h2 style={{ color:'var(--text)', margin:0, fontSize:18 }}>All Tenders ({sortedFiltered.length})</h2>
              {selectedIds.length > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px', background:'rgba(6,182,212,0.08)', border:'1px solid rgba(6,182,212,0.25)', borderRadius:8, fontSize:12 }}>
                  <span style={{ color:'var(--text)', fontWeight:600 }}>{selectedIds.length} selected</span>
                  <button onClick={() => setSelectedIds([])} style={{ padding:'3px 10px', borderRadius:4, border:'1px solid var(--border)', background:'transparent', color:'var(--text-muted)', fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>Clear</button>
                  {['manager','director'].includes(user?.role) && (
                    <button onClick={handleBulkArchive} style={{ padding:'3px 10px', borderRadius:4, border:'1px solid rgba(251,191,36,0.4)', background:'transparent', color:'#fbbf24', fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>Archive</button>
                  )}
                  {user?.role === 'director' && (
                    <button onClick={handleBulkDelete} style={{ padding:'3px 10px', borderRadius:4, border:'1px solid rgba(239,68,68,0.4)', background:'rgba(239,68,68,0.08)', color:'#f87171', fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>Permanently Delete</button>
                  )}
                </div>
              )}
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <button onClick={loadAll}
                  style={{ padding:'7px 14px', background:'transparent', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-muted)', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                  ↻ Refresh
                </button>
                {!isReadOnly && (
                <button onClick={() => { setEditTender(null); setShowModal(true); }}
                  style={{ padding:'7px 14px', background:'#3b82f6', border:'none', borderRadius:8, color:'#fff', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontWeight:600 }}>
                  <Plus size={15}/> New Tender
                </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
              <div style={{ position:'relative', flex:1, minWidth:200 }}>
                <Search size={15} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search project, client, ref…"
                  style={{ width:'100%', paddingLeft:32, paddingRight:12, paddingTop:8, paddingBottom:8, background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:12, boxSizing:'border-box' }} />
              </div>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ padding:'8px 12px', background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:12 }}>
                <option value="All">All Statuses</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filterEst} onChange={e => setFilterEst(e.target.value)}
                style={{ padding:'8px 12px', background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:12 }}>
                <option value="All">All Estimators</option>
                {estimators.map(e => <option key={e.id} value={e.initials}>{e.initials}</option>)}
              </select>
            </div>

            {/* Table */}
            <div style={{ background:'var(--card-bg)', borderRadius:12, border:'1px solid var(--border)', overflowX:'auto' }}>
              <table style={{ width:'100%', minWidth:1100, borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid var(--border)' }}>
                    <th style={{ padding:'8px 10px', width:28 }}>
                      <input type="checkbox" checked={selectedIds.length > 0 && selectedIds.length === sortedFiltered.length}
                        onChange={() => toggleSelectAll(sortedFiltered.map(t => t.id))}
                        style={{ cursor:'pointer' }} />
                    </th>
                    <th style={{ padding:'8px 10px', textAlign:'left', whiteSpace:'nowrap', width:90 }}><SortBtn field="project_ref" label="Ref" /></th>
                    <th style={{ padding:'8px 10px', textAlign:'left', minWidth:160 }}><SortBtn field="project_name" label="Project" /></th>
                    <th style={{ padding:'8px 10px', textAlign:'left', minWidth:120 }}><SortBtn field="client" label="Client" /></th>
                    <th style={{ padding:'8px 10px', textAlign:'left', width:110 }}>Classification</th>
                    <th style={{ padding:'8px 10px', textAlign:'left', width:50 }}><SortBtn field="estimator_initials" label="Est." /></th>
                    <th style={{ padding:'8px 10px', textAlign:'left', width:130 }}><SortBtn field="status" label="Status" /></th>
                    <th style={{ padding:'8px 10px', textAlign:'left', width:95, whiteSpace:'nowrap' }}><SortBtn field="deadline" label="Deadline" /></th>
                    {/* Viewer swaps £ columns for non-commercial context (address / W3W / scope). */}
                    {!isViewer && <th style={{ padding:'8px 10px', textAlign:'right', width:100, whiteSpace:'nowrap' }}>Estimate</th>}
                    {!isViewer && canSeeAV && <th style={{ padding:'8px 10px', textAlign:'right', width:80 }}>AV</th>}
                    {!isViewer && canSeeAV && <th style={{ padding:'8px 10px', textAlign:'right', width:95 }}>Premium</th>}
                    {isViewer && <th style={{ padding:'8px 10px', textAlign:'left', width:200 }}>Site Address</th>}
                    {isViewer && <th style={{ padding:'8px 10px', textAlign:'left', width:140 }}>What3Words</th>}
                    {isViewer && <th style={{ padding:'8px 10px', textAlign:'left', width:240 }}>Scope</th>}
                    <th style={{ padding:'8px 10px', textAlign:'center', width:80 }}>Changed</th>
                    {/* Actions header sticks to the right edge for Viewer so
                        the View pill never scrolls out of the viewport on
                        narrow widths. Non-viewer table unchanged. */}
                    <th style={isViewer
                      ? { padding:'8px 10px', textAlign:'center', width:100, position:'sticky', right:0, background:'var(--card-bg)', boxShadow:'-6px 0 6px -4px rgba(0,0,0,0.12)', zIndex:2 }
                      : { padding:'8px 10px', textAlign:'center', width:100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiltered.map(t => {
                    const over = isOverdue(t);
                    const premium = (Number(t.price)||0) + (Number(t.asset_value)||0);
                    return (
                      <tr key={t.id}
                        style={{ borderBottom:'1px solid var(--border)', cursor: isViewer ? 'pointer' : 'default' }}
                        onClick={isViewer ? () => setViewerTender(t) : undefined}
                        onMouseEnter={e => e.currentTarget.style.background='var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <td style={{ padding:'7px 10px', width:28 }}>
                          <input type="checkbox" checked={selectedIds.includes(t.id)}
                            onChange={() => toggleSelect(t.id)}
                            onClick={e => e.stopPropagation()}
                            style={{ cursor:'pointer' }} />
                        </td>
                        <td style={{ padding:'7px 10px', color:'var(--text-secondary)' }}>{t.project_ref || '—'}</td>
                        <td style={{ padding:'7px 10px', color:'var(--text)', maxWidth:200 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.project_name}</div>
                        </td>
                        <td style={{ padding:'7px 10px', color:'var(--text-dim)', maxWidth:150 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.client}</div>
                        </td>
                        <td style={{ padding:'7px 10px', color:'var(--text-muted)', fontSize:11, maxWidth:130 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.classification_name || '—'}</div>
                        </td>
                        <td style={{ padding:'7px 10px', color:'var(--text-dim)' }}>{t.estimator_initials || '—'}</td>
                        <td style={{ padding:'9px 14px' }}><StatusBadge status={t.status} /></td>
                        <td style={{ padding:'7px 10px', color: over ? '#ef4444' : '#94a3b8', fontWeight: over ? 600 : 400 }}>
                          {fmtDate(t.deadline)}{over && ' ⚠'}
                        </td>
                        {!isViewer && <td style={{ padding:'7px 10px', color:'var(--text)', textAlign:'right' }}>{fmtGBP(t.price)}</td>}
                        {!isViewer && canSeeAV && <td style={{ padding:'7px 10px', color:'#60a5fa', textAlign:'right' }}>{t.idno_name ? fmtGBP(t.asset_value) : '—'}</td>}
                        {!isViewer && canSeeAV && <td style={{ padding:'7px 10px', color: premium > (Number(t.price)||0) ? '#10b981' : '#64748b', textAlign:'right', fontWeight: premium > (Number(t.price)||0) ? 600 : 400 }}>{t.price ? fmtGBP(premium) : '—'}</td>}
                        {isViewer && (
                          <td style={{ padding:'7px 10px', color:'var(--text-dim)', fontSize:11, width:200, maxWidth:200 }}>
                            {/* Fixed inner-div width so table-layout:auto can't blow the column past the cap. */}
                            <div style={{ width:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={t.site_address || ''}>
                              {t.site_address || <span style={{ color:'var(--text-muted)', fontStyle:'italic' }}>—</span>}
                            </div>
                          </td>
                        )}
                        {isViewer && (
                          <td style={{ padding:'7px 10px', color: t.what3words ? '#10b981' : 'var(--text-muted)', fontSize:11, fontFamily:"'DM Mono', monospace", width:140, maxWidth:140 }}>
                            <div style={{ width:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={t.what3words ? '///' + t.what3words : ''}>
                              {t.what3words ? '///' + t.what3words : <span style={{ fontStyle:'italic' }}>—</span>}
                            </div>
                          </td>
                        )}
                        {isViewer && (
                          <td style={{ padding:'7px 10px', color:'var(--text-dim)', fontSize:11, width:240, maxWidth:240, cursor:'pointer' }}
                              onClick={() => setViewerTender(t)} title="Click to view full scope">
                            {/* Truncated scope with visible '…more' link-styled affordance so the click-to-expand isn't invisible. */}
                            <div style={{ width:220, display:'flex', alignItems:'center', gap:6, overflow:'hidden' }}>
                              <div style={{ flex:'1 1 auto', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={t.initial_notes || ''}>
                                {t.initial_notes || <span style={{ color:'var(--text-muted)', fontStyle:'italic' }}>—</span>}
                              </div>
                              {t.initial_notes && t.initial_notes.length > 32 && (
                                <span style={{ flex:'0 0 auto', color:'#0e9aaa', fontWeight:600, textDecoration:'underline', fontSize:10.5 }}>more</span>
                              )}
                            </div>
                          </td>
                        )}
                        <td style={{ padding:'7px 10px', textAlign:'center' }}>
                          <div style={{ display:'flex', gap:4, justifyContent:'center', alignItems:'center', flexWrap:'wrap' }}>
                            {t.last_changed_by && (
                              <span style={{ background:'var(--surface2)', borderRadius:4, padding:'2px 6px', color:'var(--text-muted)', fontSize:10 }}>{t.last_changed_by}</span>
                            )}
                            {t.rejection_count > 0 && (
                              <span style={{ background:'rgba(249,115,22,0.12)', border:'1px solid rgba(249,115,22,0.3)', borderRadius:4, padding:'2px 6px', color:'#fb923c', fontSize:10, fontWeight:600 }}>Rev {t.rejection_count}</span>
                            )}
                          </div>
                        </td>
                        <td style={isViewer
                            ? { padding:'7px 10px', textAlign:'center', position:'sticky', right:0, background:'var(--card-bg)', boxShadow:'-6px 0 6px -4px rgba(0,0,0,0.12)', zIndex:1 }
                            : { padding:'7px 10px', textAlign:'center' }}
                            onClick={isViewer ? e => e.stopPropagation() : undefined}>
                          <div style={{ display:'flex', gap:4, justifyContent:'center' }}>
                            {/* Viewer gets a dedicated lightweight info modal (address/W3W/scope only, zero commercial). Everyone else uses the full TenderModal. */}
                            {isViewer ? (
                              <button onClick={e => { e.stopPropagation(); setViewerTender(t); }} title="View full detail — address, W3W, scope"
                                style={{ padding:'4px 8px', background:'rgba(14,154,170,0.14)', border:'1px solid rgba(14,154,170,0.35)', borderRadius:5, color:'#0e9aaa', cursor:'pointer', display:'flex', alignItems:'center', gap:4, fontSize:11, fontWeight:500 }}>
                                <Eye size={13}/><span>View</span>
                              </button>
                            ) : (
                              <button onClick={() => openEdit(t)} title={isReadOnly ? 'View' : 'Edit'}
                                style={{ padding:5, background:'rgba(59,130,246,0.1)', border:'none', borderRadius:6, color:'#60a5fa', cursor:'pointer' }}><Edit2 size={13}/></button>
                            )}
                            {!isViewer && (
                              <button onClick={() => openActivityLog(t)} title="Activity log"
                                style={{ padding:5, background:'var(--surface2)', border:'none', borderRadius:6, color:'var(--text-dim)', cursor:'pointer' }}><Clock size={13}/></button>
                            )}
                            {t.status === 'Won' && (
                              <button onClick={() => setCrTender(t)} title="Contract Review"
                                style={{ padding:5, background: t.contract_review_status === 'complete' ? 'rgba(16,185,129,0.1)' : 'rgba(234,179,8,0.1)', border:'none', borderRadius:6, color: t.contract_review_status === 'complete' ? '#10b981' : '#f59e0b', cursor:'pointer', fontSize:9, fontWeight:700 }}>CR</button>
                            )}
                            {['manager','director'].includes(user?.role) && !t.is_archived && (
                              <button onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Archive "${t.project_name}"? It will move to the Archive view. (Recoverable via Restore.)`)) return;
                                try {
                                  await api.archiveTender(t.id);
                                  loadAll();
                                } catch (err) { alert(err.message || 'Archive failed'); }
                              }} title="Archive"
                                style={{ padding:5, background:'rgba(251,191,36,0.1)', border:'none', borderRadius:6, color:'#fbbf24', cursor:'pointer' }}><Archive size={13}/></button>
                            )}
                            {isManager && (
                              <button onClick={() => handleDelete(t.id)} title="Delete"
                                style={{ padding:5, background:'rgba(239,68,68,0.1)', border:'none', borderRadius:6, color:'#ef4444', cursor:'pointer' }}><Trash2 size={13}/></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {sortedFiltered.length === 0 && (
                <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>No tenders match current filters</div>
              )}
            </div>
          </div>
        )}
        {tab === 'archive' && (
          <ArchivePage currentUser={user} />
        )}

        {tab === 'crm' && (
          <CRMPipeline />
        )}
        {tab === 'import' && (
          <ImportPage />
        )}

        {tab === 'admin' && isManager && (
          <AdminCentre currentUserId={user.id} />
        )}

        {tab === 'directory' && (
          <div style={{ padding: 20 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button onClick={() => setDirectoryView('companies')}
                style={{ padding: '8px 16px', background: directoryView === 'companies' ? 'var(--teal)' : 'transparent', color: directoryView === 'companies' ? '#fff' : 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
                Companies
              </button>
              <button onClick={() => setDirectoryView('contacts')}
                style={{ padding: '8px 16px', background: directoryView === 'contacts' ? 'var(--teal)' : 'transparent', color: directoryView === 'contacts' ? '#fff' : 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
                Contacts
              </button>
            </div>
            {directoryView === 'companies'
              ? <CompanyDirectory user={user} />
              : <ContactDirectory user={user} />}
          </div>
        )}
      </main>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="footer" style={{ justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <img src={theme === 'dark' ? LOGO_STACKED_WHITE : LOGO_STACKED_BLACK} alt="Ingenious" style={{ height:32, opacity:0.5 }} />
          {/* P4 security-sweep: Archive tab hidden from Viewer (no view_archive cap).
              Server-side guard on GET /api/archive mirrors this. */}
          {(user?.capabilities?.includes('view_archive')
            || (!user?.capabilities && user?.role !== 'viewer')) && (
            <button
              onClick={() => setTab('archive')}
              style={{
                display:'flex', alignItems:'center', gap:5,
                padding:'4px 12px', borderRadius:5, fontSize:12,
                background: tab==='archive' ? 'rgba(14,154,170,0.12)' : 'transparent',
                border: tab==='archive' ? '1px solid rgba(14,154,170,0.35)' : '1px solid transparent',
                color: tab==='archive' ? '#0e9aaa' : 'var(--text-muted)',
                cursor:'pointer'
              }}>
              <Archive size={12}/> Archive
            </button>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ color:'var(--text-muted)', fontSize:11 }}>Field guidance</span>
          <button onClick={() => {
            const next = !showTips;
            setShowTips(next);
            localStorage.setItem('ipe_tips', next ? 'on' : 'off');
          }} style={{ position:'relative', width:36, height:20, borderRadius:10, border:'none', cursor:'pointer',
            background: showTips ? 'var(--teal)' : 'var(--border)', transition:'background 0.2s' }}>
            <span style={{ position:'absolute', top:2, left: showTips ? 18 : 2, width:16, height:16, borderRadius:'50%',
              background:'#fff', transition:'left 0.2s' }} />
          </button>
          <div style={{ width:1, height:20, background:'var(--border)', margin:'0 4px' }} />
          <span className="toggle-label">☀</span>
          <button className="theme-toggle" onClick={toggleTheme} title="Toggle theme" />
          <span className="toggle-label">🌙</span>
        </div>
      </footer>

      {/* Password change modal */}
      {showPwModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width:360 }}>
            <h3 style={{ color:'var(--text)', margin:'0 0 20px', fontSize:16 }}>Change Password</h3>
            {['current','next','confirm'].map((field, i) => (
              <div key={field} style={{ marginBottom:12 }}>
                <label style={{ color:'var(--text-muted)', fontSize:11, display:'block', marginBottom:4 }}>
                  {field === 'current' ? 'Current Password' : field === 'next' ? 'New Password' : 'Confirm New Password'}
                </label>
                <input type="password" value={pwForm[field]}
                  onChange={e => setPwForm(f => ({ ...f, [field]: e.target.value }))}
                  style={{ width:'100%', padding:'8px 12px', borderRadius:8, fontSize:13, boxSizing:'border-box' }}/>
              </div>
            ))}
            <p style={{ color:'var(--text-dim)', fontSize:10, margin:'-4px 0 12px', lineHeight:1.5 }}>
              Min 10 chars · uppercase · number · special character
            </p>
            {pwError && <p style={{ color:'var(--red)', fontSize:12, margin:'0 0 12px' }}>{pwError}</p>}
            {pwSuccess && <p style={{ color:'var(--green)', fontSize:12, margin:'0 0 12px' }}>✓ Password changed successfully</p>}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
              <button className="btn btn-ghost" onClick={() => setShowPwModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleChangePassword} disabled={pwSaving}>
                {pwSaving ? 'Saving…' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkClassify && isManager && (
        <BulkClassify onClose={() => { setShowBulkClassify(false); loadAnalytics(); }} />
      )}
      {viewerTender && (
        <TenderInfoModal tender={viewerTender} onClose={() => setViewerTender(null)} />
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {showCommission && (
        <CommissionDrawer onClose={() => setShowCommission(false)} />
      )}
      {showModal && (
        <TenderModal
          tender={editTender}
          estimators={estimators}
          idnos={idnos}
          classifications={classifications}
          onSave={handleSave}
          showTips={showTips}
          readOnly={isReadOnly}
          onClose={() => { setShowModal(false); setEditTender(null); }}
        />
      )}

      {crTender && (
        <ContractReviewModal
          tenderId={crTender.id}
          tenderName={crTender.project_name}
          onClose={() => setCrTender(null)}
          onComplete={() => { setCrTender(null); loadAll(); }}
        />
      )}

      {viewActivity && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--card-bg)', borderRadius:16, width:'100%', maxWidth:520, maxHeight:'80vh', overflow:'auto', padding:24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <h3 style={{ color:'var(--text)', margin:0, fontSize:16 }}>Activity Log</h3>
                <p style={{ color:'var(--text-muted)', fontSize:12, margin:'2px 0 0' }}>{viewActivity.project_name}</p>
              </div>
              <button onClick={() => setViewActivity(null)} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer' }}>✕</button>
            </div>
            <ActivityLog entries={viewActivity.activity_log || []} />
          </div>
        </div>
      )}
    </div>


    </>
    </UserProvider>
  );

}
